import { useState, useEffect } from "react";
// TODO: restore BellRing once the dashboard email backend is ready.
import { Settings, Clock, Filter, MailCheck, SlidersHorizontal } from "lucide-react";
import { useSettingsStore } from "@/store/settingsStore";
import { useKeycloak } from "@/modules/auth/context/KeycloakContext";
import { profileService, type ProfileResponse } from "@/services/profileService";
import { Reveal } from "@/components/atoms/Reveal";
import { AccordionSection } from "@/components/molecules/AccordionSection";
import { ViewingSettingsSection } from "@/modules/settings/sections/ViewingSettingsSection";
import { LeadsProcessingSection } from "@/modules/settings/sections/LeadsProcessingSection";
import { LiveHoursSection } from "@/modules/settings/sections/LiveHoursSection";
import { AutoAcceptEnquirySection } from "@/modules/settings/sections/AutoAcceptEnquirySection";
// TODO: restore once the dashboard email backend is ready.
// import { DashboardEmailSection } from "@/modules/settings/sections/DashboardEmailSection";
import { flattenEntities } from "@/modules/settings/entityHelpers";

/**
 * Settings hub. Every settings group is its own accordion with the scope
 * selects it needs and its own save button — the groups are independent, so
 * changing scope inside one never affects another (or the app-wide header
 * context). A lone section renders expanded.
 */
export function SettingsPage() {
  const { keycloak } = useKeycloak();
  const email = keycloak?.tokenParsed?.email as string | undefined;

  const { legalEntityId, branchId, setLegalEntity } = useSettingsStore();
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email) return;
    setLoading(true);
    void profileService
      .get(email)
      .then((res) => {
        setProfile(res);
        // Keep the app-wide context pointing at a legal entity that still exists.
        const flat = flattenEntities(res.legalEntities);
        const isValid = flat.some((le) => le.id === legalEntityId);
        if ((!legalEntityId || !isValid) && flat.length > 0) {
          const first = flat[0];
          setLegalEntity(first.id, first.name);
        }
      })
      .finally(() => setLoading(false));
  }, [email, legalEntityId, setLegalEntity]);

  const legalEntities = profile?.legalEntities ?? [];

  // Sections registered on this page. Add an entry here to introduce a new
  // settings group — a single registered section starts expanded.
  const sections = [
    {
      id: "viewing",
      title: "Viewing Settings",
      description: "Slot rules used when generating availability for viewings.",
      icon: <Clock size={16} />,
      render: () => (
        <ViewingSettingsSection
          legalEntities={legalEntities}
          initialLegalEntityId={legalEntityId}
          initialBranchId={branchId}
        />
      ),
    },
    {
      id: "leads-processing",
      title: "Leads Processing",
      description:
        "Automated contact creation and reachout for incoming leads, per category.",
      icon: <Filter size={16} />,
      render: () => (
        <LeadsProcessingSection
          legalEntities={legalEntities}
          initialLegalEntityId={legalEntityId}
          initialBranchId={branchId}
        />
      ),
    },
    {
      id: "auto-accept-enquiry",
      title: "Auto-Accept Enquiries",
      description:
        "Accept incoming enquiries automatically, per legal entity or branch.",
      icon: <MailCheck size={16} />,
      render: () => (
        <AutoAcceptEnquirySection
          legalEntities={legalEntities}
          initialLegalEntityId={legalEntityId}
          initialBranchId={branchId}
        />
      ),
    },
    {
      id: "live-hours",
      title: "Live Hours",
      description: "Configure the live hours for each legal entity or branch.",
      icon: <SlidersHorizontal size={16} />,
      render: () => (
        <LiveHoursSection legalEntities={legalEntities} />
      ),
    },
    // TODO: Hidden until the dashboard email backend is ready.
    // {
    //   id: "dashboard-email",
    //   title: "Client Dashboard Email",
    //   description:
    //     "Schedule the client dashboard email — frequency, day, time and recipients.",
    //   icon: <BellRing size={16} />,
    //   render: () => (
    //     <DashboardEmailSection
    //       legalEntities={legalEntities}
    //       initialLegalEntityId={legalEntityId}
    //     />
    //   ),
    // },
  ];

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() =>
    sections.length === 1 ? { [sections[0].id]: true } : {},
  );

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b border-bridge-border bg-white px-6 pb-6 pt-6">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-bridge-accent/10 p-2">
            <Settings size={20} className="text-bridge-accent" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-bridge-fg">Settings</h1>
            <p className="text-xs text-bridge-muted">
              Manage each settings group independently
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      {/* One scroll container: the page. Sections grow with their content and
          pin the parts that must stay reachable (scope, actions, preview)
          with `sticky`. An inner fixed-height pane fought this scroll and
          clipped whenever the viewport got shorter than it expected.

          `scrollbar-gutter: stable` keeps the gutter reserved whether or not a
          scrollbar is showing, so opening a section doesn't reflow the page.

          The vertical padding belongs to the content, not to this container:
          padding is part of the scrollport, so as top padding it left a band
          under the page header that a section's own content scrolled visibly
          through — above anything that section pinned with `sticky top-0`. */}
      <div className="flex-1 overflow-y-auto bg-bridge-surface/40 px-6 [scrollbar-gutter:stable]">
        <div className="relative mb-24 mt-6 w-full space-y-4">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/60 backdrop-blur-[1px]">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-bridge-accent border-t-transparent" />
            </div>
          )}

          {sections.map((section, i) => (
            <Reveal key={section.id} delay={i * 70}>
            <AccordionSection
              icon={section.icon}
              title={section.title}
              description={section.description}
              open={Boolean(openSections[section.id])}
              onToggle={(next) =>
                setOpenSections((prev) => ({ ...prev, [section.id]: next }))
              }
            >
              {section.render()}
            </AccordionSection>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
